import React, { useEffect, useMemo, useState } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  Typography,
} from '@mui/material';

import { useMerchant } from '../../../contexts/MerchantContext';
import { useIsSkipifyAdmin } from '../../../hooks';
import {
  useGetAgenticSettingsQuery,
  useGetAgentMetricsQuery,
  useGetAgentWidgetMetricsQuery,
  useGetOrderAttributionMetricsQuery,
} from '../../../store/modules/merchant/api';
import { DateRangeValue, resolvePresetRange } from './dashboard/DateRangeSelector';
import { ALL_AGENTS_VIEW } from './dashboard/AgentViewSelect';
import { AgentFunnelPanel } from './dashboard/AgentFunnelPanel';
import { AgentMilestonesPanel } from './dashboard/AgentMilestonesPanel';
import { AgentFilterToolbar } from './dashboard/AgentFilterToolbar';
import { IntentTagPanel } from './dashboard/IntentTagPanel';
import { MetricKpiCard } from './dashboard/MetricKpiCard';
import { PromptDriftPanel } from './dashboard/PromptDriftPanel';
import { OrderAttributionPanel } from './dashboard/OrderAttributionPanel';
import {
  describeResponseLatency,
  formatCount,
  formatDuration,
  formatRate,
} from './dashboard/formatMetrics';

const AGENT_NAME = 'Ella';

const queryErrorMessage = (isError: boolean, error: unknown, fallback: string): string | null => {
  if (!isError) {
    return null;
  }
  const errors = (error as { data?: { errors?: string[] } })?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(' ');
  }
  return fallback;
};

const AdvancedAnalyticsSection = ({
  loading,
  toolAccuracyRate,
  reliabilityRate,
  groundedResponseRate,
  latency,
}: {
  loading: boolean;
  toolAccuracyRate?: number | null;
  reliabilityRate?: number | null;
  groundedResponseRate?: number | null;
  latency: { value: string; description: string };
}) => (
  <Accordion
    disableGutters
    elevation={0}
    data-testid="ella-advanced-analytics"
    sx={{
      border: '1px solid',
      borderColor: 'secondary.main',
      borderRadius: '8px',
      backgroundColor: 'background.paper',
      backgroundImage: 'none',
      overflow: 'hidden',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 1.5 }}>
      <Box>
        <Typography variant="medium">Advanced Analytics</Typography>
        <Typography variant="small" color="text.secondary" component="p">
          Skipify admins only. Merchants do not see this.
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Tool Accuracy"
            value={formatRate(toolAccuracyRate)}
            description="Correct tool per turn"
            infoTooltip="Share of situations where our analysis found the selected tool fit the shopper’s request."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Reliability"
            value={formatRate(reliabilityRate)}
            description="Tool calls without error"
            infoTooltip="Share of tool calls that ran successfully and finished without a reported error."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Grounded Response Rate"
            value={formatRate(groundedResponseRate)}
            description="Answers tied to page or RAG"
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Avg Latency"
            value={latency.value}
            description={latency.description}
            infoTooltip="Median wait from when a shopper's message arrives to the first words of a reply."
            isLoading={loading}
          />
        </Grid>
      </Grid>
    </AccordionDetails>
  </Accordion>
);

export const DashboardTab = () => {
  const { merchantId } = useMerchant();
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => resolvePresetRange('7d'));
  const [excludeTestSessions, setExcludeTestSessions] = useState(false);
  const [selectedPersonalityId, setSelectedPersonalityId] = useState(ALL_AGENTS_VIEW);

  const { data: agenticSettings } = useGetAgenticSettingsQuery(merchantId ?? '', {
    skip: !merchantId,
  });

  const personalities = useMemo(
    () => agenticSettings?.agents?.Ella?.personalities ?? [],
    [agenticSettings?.agents?.Ella?.personalities],
  );

  useEffect(() => {
    if (selectedPersonalityId === ALL_AGENTS_VIEW) {
      return;
    }
    const stillExists = personalities.some(
      (personality) => personality.personalityId === selectedPersonalityId,
    );
    if (!stillExists) {
      setSelectedPersonalityId(ALL_AGENTS_VIEW);
    }
  }, [personalities, selectedPersonalityId]);

  const queryArgs = useMemo(
    () => ({
      merchantId: merchantId ?? '',
      agentName: AGENT_NAME,
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString(),
      excludeTestSessions,
      ...(selectedPersonalityId !== ALL_AGENTS_VIEW
        ? { personalityIds: [selectedPersonalityId] }
        : {}),
    }),
    [merchantId, dateRange.from, dateRange.to, excludeTestSessions, selectedPersonalityId],
  );

  const funnelArgs = useMemo(
    () => ({
      merchantId: merchantId ?? '',
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      excludeTestSessions,
    }),
    [merchantId, dateRange.from, dateRange.to, excludeTestSessions],
  );

  const attributionArgs = useMemo(
    () => ({
      merchantId: merchantId ?? '',
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      ...(selectedPersonalityId !== ALL_AGENTS_VIEW
        ? { personalityIds: [selectedPersonalityId] }
        : {}),
    }),
    [merchantId, dateRange.from, dateRange.to, selectedPersonalityId],
  );

  const { data, isLoading, isFetching, isError, error } = useGetAgentMetricsQuery(queryArgs, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: funnelData,
    isLoading: funnelLoading,
    isFetching: funnelFetching,
    isError: funnelIsError,
    error: funnelError,
  } = useGetAgentWidgetMetricsQuery(funnelArgs, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: attributionData,
    isLoading: attributionLoading,
    isFetching: attributionFetching,
    isError: attributionIsError,
    error: attributionError,
  } = useGetOrderAttributionMetricsQuery(attributionArgs, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });
  const loading = isLoading || isFetching;

  const latency = describeResponseLatency(data?.responseLatency);

  const errorMessage = queryErrorMessage(isError, error, 'Failed to load agent metrics');
  const funnelErrorMessage = queryErrorMessage(
    funnelIsError,
    funnelError,
    'Could not load launcher activity.',
  );
  const attributionErrorMessage = queryErrorMessage(
    attributionIsError,
    attributionError,
    'Could not load order attribution.',
  );

  return (
    <Box data-testid="ella-dashboard-tab">
      <AgentFilterToolbar
        personalities={personalities}
        selectedPersonalityId={selectedPersonalityId}
        onPersonalityChange={setSelectedPersonalityId}
        excludeTestSessions={excludeTestSessions}
        onExcludeTestSessionsChange={setExcludeTestSessions}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        disabled={loading}
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Sessions"
            value={formatCount(data?.sessions)}
            description="Total shopper sessions"
            infoTooltip="Each session is one shopper conversation. If they leave and come back to the same conversation, it still counts as one."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Repeat Usage"
            value={formatRate(data?.repeatUsageRate)}
            description="Shoppers who came back"
            infoTooltip="Share of recognized devices that came back for more than one session in the selected date range. Sessions from devices we cannot recognize are left out."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Avg Session Time"
            value={formatDuration(data?.avgSessionDurationSec)}
            description="Average per sessions"
            infoTooltip="Average time each shopper conversation stayed open, including quiet."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Avg Time-to-Checkout"
            value={formatDuration(data?.timeToCheckoutSec)}
            description="Discovery to checkout"
            infoTooltip="Average time from conversation start to the first checkout handoff. Conversations that never hand off are omitted."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Checkout Initiation Rate"
            value={formatRate(data?.checkoutConversionRate)}
            description="Sessions handed to checkout"
            infoTooltip="Share of sessions where the shopping agent sent the shopper to checkout at least once. Extra handoffs in the same session count once."
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricKpiCard
            title="Task Completion Rate"
            value={formatRate(data?.taskCompletionRate)}
            description="Shopper goals completed"
            isLoading={loading}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          <OrderAttributionPanel
            data={attributionData}
            isLoading={attributionLoading || attributionFetching}
            errorMessage={attributionErrorMessage}
          />
          <AgentMilestonesPanel
            milestones={data?.agentMilestones}
            sessionCount={data?.sessions}
            isLoading={loading}
          />
        </Box>
        <AgentFunnelPanel
          personalities={personalities}
          data={funnelData}
          isLoading={funnelLoading || funnelFetching}
          errorMessage={funnelErrorMessage}
        />
        <IntentTagPanel items={data?.analysis?.intentTags?.items} isLoading={loading} />
        <PromptDriftPanel items={data?.analysis?.promptDrift?.items} isLoading={loading} />
        {isSkipifyAdmin && (
          <AdvancedAnalyticsSection
            loading={loading}
            toolAccuracyRate={data?.toolAccuracyRate}
            reliabilityRate={data?.reliabilityRate}
            groundedResponseRate={data?.groundedResponseRate}
            latency={latency}
          />
        )}
      </Box>
    </Box>
  );
};
